import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, useDateRange } from '../components/DateRangeFilter';
import { formatINR, formatDate, apiError } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Download,
  Eye,
  Edit2,
  Trash2,
  Loader2,
  CircleDollarSign,
} from 'lucide-react';

const STATUSES = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];

const STATUS_STYLES = {
  draft:   'bg-gray-100 text-gray-700',
  sent:    'bg-gray-200 text-gray-800',
  paid:    'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500 line-through',
};

export default function Invoices() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [downloading, setDownloading] = useState(null);
  const [range, rangeState, setRangeState] = useDateRange('priceintel.invoicesRange', 'this_month');

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await invoiceAPI.getAll();
      setInvoices(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to fetch invoices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownloadPdf = async (inv) => {
    setDownloading(inv.id);
    try {
      const response = await invoiceAPI.downloadPdf(inv.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${(inv.invoice_number || inv.id.slice(0, 8)).replace(/[^\w.-]+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, 'PDF download failed'));
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await invoiceAPI.delete(inv.id);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  const filtered = invoices.filter((inv) => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    // Date filter — apply only when a range is set
    if (range.start && (inv.issued_at || '') < range.start) return false;
    if (range.end && (inv.issued_at || '') > range.end) return false;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      const hay = `${inv.invoice_number || ''} ${inv.client_name || ''} ${inv.subject || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // KPIs — issued invoices only (no drafts, no cancelled); Paid counts part-payments too.
  const issued = filtered.filter((i) => i.status !== 'draft' && i.status !== 'cancelled');
  const drafts = filtered.filter((i) => i.status === 'draft').length;
  const cancelledCount = filtered.filter((i) => i.status === 'cancelled').length;
  const total = issued.reduce((s, i) => s + (i.total || 0), 0);
  const paid = issued.reduce((s, i) => s + (i.payment_made || 0), 0);
  const outstanding = issued.reduce((s, i) => s + (i.balance_due || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Invoices</h1>
          <p className="text-gray-500 mt-1">Invoices for DESENO (Partnership).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter value={rangeState} onChange={setRangeState} />
          {can('write') && (
            <Link to="/invoices/new">
              <Button className="bg-black hover:bg-black/90">
                <Plus className="w-4 h-4 mr-1" /> New invoice
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total invoiced</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatINR(total)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {issued.length} invoice{issued.length === 1 ? '' : 's'}
              {drafts > 0 && ` · ${drafts} draft${drafts === 1 ? '' : 's'} not counted`}
              {cancelledCount > 0 && ` · ${cancelledCount} cancelled not counted`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Paid</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-700">{formatINR(paid)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</p>
            <p className="text-2xl font-bold font-mono mt-1 text-red-700">{formatINR(outstanding)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by invoice #, client, or subject"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">
              {invoices.length === 0
                ? (can('write')
                  ? <>No invoices yet. <Link to="/invoices/new" className="text-black underline">Create your first one</Link>.</>
                  : 'No invoices yet.')
                : 'No invoices match the current filters.'}
            </div>
          ) : (
            <>
            {/* Phones: one card per invoice, everything that matters visible without sideways scrolling */}
            <ul className="md:hidden divide-y" data-testid="invoices-phone-list">
              {filtered.map((inv) => (
                <li key={inv.id} className="p-4 space-y-2 cursor-pointer active:bg-gray-50" onClick={() => navigate(`/invoices/${inv.id}`)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium break-words">{inv.client_name}</p>
                      <p className="text-xs text-gray-500 font-mono">{inv.invoice_number} · {formatDate(inv.issued_at)}</p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div className="text-sm min-w-0">
                      <p className={`font-mono font-medium ${inv.status === 'cancelled' ? 'line-through text-gray-400' : ''}`}>{formatINR(inv.total)}</p>
                      <p className="text-xs mt-0.5">
                        {inv.balance_due > 0
                          ? <span className="text-red-700">Balance {formatINR(inv.balance_due)} · due {formatDate(inv.due_at)}</span>
                          : <span className="text-gray-400">No balance</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      {can('write') && inv.status !== 'cancelled' && inv.balance_due > 0.01 && (
                        <Button variant="outline" size="icon" className="h-9 w-9 text-green-700" onClick={() => navigate(`/invoices/${inv.id}?action=pay`)} aria-label="Record payment" title="Record payment">
                          <CircleDollarSign className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => handleDownloadPdf(inv)} disabled={downloading === inv.id} aria-label="Download PDF" title="Download PDF">
                        {downloading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left text-gray-500 whitespace-nowrap">
                    <th className="py-3 px-3 font-medium">Invoice #</th>
                    <th className="py-3 px-3 font-medium">Client</th>
                    <th className="py-3 px-3 font-medium">Issued</th>
                    <th className="py-3 px-3 font-medium">Due</th>
                    <th className="py-3 px-3 font-medium text-right">Total</th>
                    <th className="py-3 px-3 font-medium text-right">Balance</th>
                    <th className="py-3 px-3 font-medium text-center">Status</th>
                    <th className="py-3 px-3 w-[180px]"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => (
                    <tr key={inv.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-3 px-3 font-mono text-xs font-medium whitespace-nowrap">{inv.invoice_number}</td>
                      <td className="py-3 px-3 font-medium min-w-[160px]">{inv.client_name}</td>
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{formatDate(inv.issued_at)}</td>
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{formatDate(inv.due_at)}</td>
                      <td className={`py-3 px-3 text-right font-mono whitespace-nowrap ${inv.status === 'cancelled' ? 'line-through text-gray-400' : ''}`}>{formatINR(inv.total)}</td>
                      <td className="py-3 px-3 text-right font-mono whitespace-nowrap">
                        {inv.balance_due > 0
                          ? <span className="text-red-700">{formatINR(inv.balance_due)}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {can('write') && inv.status !== 'cancelled' && inv.balance_due > 0.01 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/invoices/${inv.id}?action=pay`)}
                              title="Record payment"
                              className="text-green-700 hover:text-green-800 hover:bg-green-50"
                            >
                              <CircleDollarSign className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/invoices/${inv.id}`)} title="View">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {can('write') && inv.status !== 'cancelled' && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/invoices/${inv.id}/edit`)} title="Edit">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(inv)} disabled={downloading === inv.id} title="Download PDF">
                            {downloading === inv.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Download className="w-3.5 h-3.5" />}
                          </Button>
                          {can('delete') && (
                            <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800" onClick={() => handleDelete(inv)} title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
